import React from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  Flex,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  Title
} from '@mantine/core';
import {
  TbAlertTriangle,
  TbChevronDown,
  TbChevronRight,
  TbCircleCheck,
  TbMapPin,
  TbMessageCircle,
  TbSparkles
} from 'react-icons/tb';
import { useSuggestReportReviewFix } from '../../../../services/report.service';
import {
  useExplainReportReviewFinding,
  useSummarizeReportReview
} from '../../../../services/agent.service';
import { idCreator } from '../../../Project/utils/idCreator';
import { extractApiError } from '../../../../utils';

const severityColor = {
  critical: 'red',
  warning: 'yellow',
  info: 'blue'
};

const severityLabel = {
  critical: 'Critical',
  warning: 'Warning',
  info: 'Info'
};

function formatReviewedAt(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
}

function formatEvidenceLabel(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
}

function formatReportName(value) {
  if (value === null || value === undefined || value === '') return null;
  return String(value)
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, char => char.toUpperCase());
}

function getLocationLabel(key) {
  if (key === 'table' || key === 'table_name') return 'Section';
  if (key === 'row_index') return 'Row';
  if (key === 'field') return 'Field';
  return formatEvidenceLabel(key);
}

function getLocationValue(row) {
  if (row.key === 'table' || row.key === 'table_name' || row.key === 'field') {
    return formatReportName(row.value) || row.value;
  }
  if (row.key === 'row_index') {
    const numericValue = Number(row.value);
    return Number.isNaN(numericValue) ? row.value : numericValue + 1;
  }
  return row.value;
}

function buildLocationSentence(locationRows) {
  const section = locationRows.find(row => row.key === 'table' || row.key === 'table_name');
  const row = locationRows.find(item => item.key === 'row_index');
  const field = locationRows.find(item => item.key === 'field');

  const parts = [];
  if (section) parts.push(`the ${getLocationValue(section)} section`);
  if (row) parts.push(`row ${getLocationValue(row)}`);
  if (field) parts.push(`field ${getLocationValue(field)}`);

  if (!parts.length) return null;
  return `Go to ${parts.join(' and review ')}.`;
}

function formatEvidenceValue(value) {
  if (value === null || value === undefined || value === '') return 'Empty';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'object') {
    if (value.msg) return value.msg;
    if (value.error_message) return value.error_message;
    return Object.entries(value)
      .map(
        ([key, nestedValue]) => `${formatEvidenceLabel(key)}: ${formatEvidenceValue(nestedValue)}`
      )
      .join(' · ');
  }
  return String(value);
}

function buildEvidenceRows(evidence = {}) {
  const rows = [];
  const normalizedEvidence = { ...evidence };
  if (evidence.required_table && typeof evidence.required_table === 'object') {
    Object.entries(evidence.required_table).forEach(([key, value]) => {
      normalizedEvidence[key] = value;
    });
    delete normalizedEvidence.required_table;
  }
  const preferredKeys = [
    'table',
    'table_name',
    'row_index',
    'field',
    'field_names',
    'value',
    'error_type',
    'error',
    'reference_key',
    'error_message',
    'name',
    'name_field',
    'lei_field',
    'missing_counterparty_lei_count'
  ];

  preferredKeys.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(normalizedEvidence, key)) {
      rows.push({
        key,
        label: formatEvidenceLabel(key),
        value: formatEvidenceValue(normalizedEvidence[key])
      });
    }
  });

  Object.keys(normalizedEvidence).forEach(key => {
    if (!preferredKeys.includes(key)) {
      rows.push({
        key,
        label: formatEvidenceLabel(key),
        value: formatEvidenceValue(normalizedEvidence[key])
      });
    }
  });

  return rows;
}

function getFindingImpact(finding) {
  if (finding.rule_id === 'DATOX-REPORT-SYSTEM-ERROR') {
    return 'This is a Datox report generation or submission-status issue. It should be fixed before the report is approved or filed.';
  }
  if (finding.rule_id === 'ESMA-DQEF-SCHEMA-VALIDATION-ERROR') {
    return 'This row does not satisfy the generated ESMA schema validation rules and may cause filing rejection or resubmission.';
  }
  if (finding.rule_id?.includes('LEI')) {
    return 'Missing LEI data is a common ESMA data-quality warning and may trigger regulator follow-up.';
  }
  if (finding.severity === 'critical') {
    return 'This issue is likely to block a clean submission and should be prioritised.';
  }
  return 'This is a contextual pre-submission warning. Review it before filing.';
}

function canOfferAIFix(finding) {
  if (finding.rule_id === 'ESMA-DQEF-REDEMPTION-FREQUENCY-WITHDRAWAL-RIGHTS') return true;
  if (finding.rule_id === 'ESMA-DQEF-NAMED-ENTITY-LEI-MISSING') return true;

  if (finding.rule_id === 'ESMA-DQEF-SCHEMA-VALIDATION-ERROR') {
    const currentValue = finding.evidence?.value;
    return (
      finding.evidence?.error_type === 'enum' &&
      typeof currentValue === 'string' &&
      currentValue.trim() !== currentValue
    );
  }

  return false;
}

function getFindingId(finding) {
  return idCreator([finding.rule_id, finding.fund_id, finding.table, finding.field].join('-'));
}

function EvidencePill({ label, value, color = 'gray' }) {
  if (value === undefined || value === null || value === '') return null;
  return (
    <Paper withBorder radius="md" p="xs" bg={`${color}.0`} style={{ flex: 1, minWidth: 160 }}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <Text size="sm" fw={700} mt={2} style={{ wordBreak: 'break-word' }}>
        {formatEvidenceValue(value)}
      </Text>
    </Paper>
  );
}

function EvidenceDetails({ evidence }) {
  const rows = buildEvidenceRows(evidence);
  if (rows.length === 0) return null;

  const errorMessage = evidence?.error?.msg || evidence?.error_message;
  const locationRows = rows.filter(row =>
    ['table', 'table_name', 'row_index', 'field'].includes(row.key)
  );
  const detailRows = rows.filter(
    row =>
      ![
        'table',
        'table_name',
        'row_index',
        'field',
        'value',
        'error_type',
        'error',
        'error_message'
      ].includes(row.key)
  );
  const locationSentence = buildLocationSentence(locationRows);

  return (
    <Paper withBorder radius="md" p="md" bg="gray.0">
      <Stack gap="sm">
        <Text size="sm" fw={700}>
          Evidence
        </Text>

        {locationRows.length > 0 && (
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>
              Location in report
            </Text>
            <Group gap="xs">
              {locationRows.map(row => (
                <Badge key={row.key} variant="outline" color="blue" size="lg">
                  {getLocationLabel(row.key)}: {getLocationValue(row)}
                </Badge>
              ))}
            </Group>
            {locationSentence && (
              <Text size="sm" c="dimmed" mt={6}>
                {locationSentence}
              </Text>
            )}
          </Box>
        )}

        <Flex gap="sm" wrap="wrap">
          <EvidencePill
            label="Current value"
            value={evidence.value}
            color={formatEvidenceValue(evidence.value) === 'Empty' ? 'red' : 'blue'}
          />
          <EvidencePill label="Error type" value={evidence.error_type} color="yellow" />
          <EvidencePill label="Reference" value={evidence.reference_key} color="blue" />
          <EvidencePill label="Entity" value={evidence.name} color="blue" />
        </Flex>

        {errorMessage && (
          <Paper withBorder radius="md" p="sm" bg="white">
            <Text size="xs" c="red" tt="uppercase" fw={700}>
              Validation message
            </Text>
            <Text size="sm" fw={600} mt={4}>
              {errorMessage}
            </Text>
          </Paper>
        )}

        {detailRows.length > 0 && (
          <Box>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb={4}>
              Additional details
            </Text>
            <Stack gap={4}>
              {detailRows.map(row => (
                <Flex key={row.key} gap="sm" align="flex-start">
                  <Text size="sm" c="dimmed" w={150} style={{ flexShrink: 0 }}>
                    {row.label}
                  </Text>
                  <Text size="sm" fw={500} style={{ wordBreak: 'break-word' }}>
                    {row.value}
                  </Text>
                </Flex>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function FindingCard({
  finding,
  index,
  onNavigateToFinding,
  onSuggestFix,
  onExplainFinding,
  isSuggestingFix,
  isExplaining
}) {
  const [isOpen, setIsOpen] = React.useState(index === 0);
  const canNavigate = Boolean(onNavigateToFinding && (finding.table || finding.evidence?.table));
  const canFix = canOfferAIFix(finding);
  const findingId = getFindingId(finding);

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="xs">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group align="flex-start" gap="xs" wrap="nowrap" style={{ flex: 1 }}>
            <Box
              component="button"
              id={`ai-report-review-finding-toggle-${findingId}`}
              type="button"
              onClick={() => setIsOpen(value => !value)}
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse finding' : 'Expand finding'}
              style={{
                background: 'transparent',
                border: 0,
                cursor: 'pointer',
                padding: 2,
                marginTop: 2,
                color: '#228be6'
              }}
            >
              {isOpen ? <TbChevronDown size={18} /> : <TbChevronRight size={18} />}
            </Box>
            <Box style={{ flex: 1 }}>
              <Text fw={600}>{finding.message}</Text>
            </Box>
          </Group>
          <Badge color={severityColor[finding.severity] || 'gray'} variant="light">
            {severityLabel[finding.severity] || finding.severity}
          </Badge>
        </Group>

        <Group gap="xs" pl={28}>
          {finding.fund_name && <Badge variant="outline">{finding.fund_name}</Badge>}
          {finding.table && <Badge variant="outline">{formatReportName(finding.table)}</Badge>}
          {finding.field && <Badge variant="outline">{formatReportName(finding.field)}</Badge>}
        </Group>

        {isOpen && (
          <Box pl={28}>
            <Divider mb="xs" />

            <Stack gap="sm">
              {canNavigate && (
                <Group justify="flex-end">
                  {canFix && (
                    <Button
                      id={`ai-report-review-finding-fix-button-${findingId}`}
                      size="xs"
                      variant="light"
                      leftSection={<TbSparkles />}
                      loading={isSuggestingFix}
                      onClick={() => onSuggestFix(finding)}
                    >
                      AI Fix
                    </Button>
                  )}
                  <Button
                    id={`ai-report-review-finding-explain-button-${findingId}`}
                    size="xs"
                    variant="light"
                    leftSection={<TbSparkles />}
                    loading={isExplaining}
                    onClick={() => onExplainFinding(finding)}
                  >
                    AI Explain
                  </Button>
                  <Button
                    id={`ai-report-review-finding-navigate-button-${findingId}`}
                    size="xs"
                    variant="light"
                    leftSection={<TbMapPin />}
                    onClick={() => onNavigateToFinding(finding)}
                  >
                    Go to report field
                  </Button>
                </Group>
              )}

              <Box>
                <Text size="sm" fw={600}>
                  Why this matters
                </Text>
                <Text size="sm" c="dimmed">
                  {getFindingImpact(finding)}
                </Text>
              </Box>

              <Box>
                <Text size="sm" fw={600}>
                  Recommendation
                </Text>
                <Text size="sm" c="dimmed">
                  {finding.recommendation}
                </Text>
              </Box>

              {finding.evidence && Object.keys(finding.evidence).length > 0 && (
                <EvidenceDetails evidence={finding.evidence} />
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

function SeverityFilterChip({ severity, color, label, count, selectedSeverity, onChange }) {
  return (
    <Badge
      id={`ai-report-review-severity-filter-${severity}`}
      component="button"
      type="button"
      aria-pressed={selectedSeverity === severity}
      color={color}
      variant={selectedSeverity === severity ? 'filled' : 'light'}
      style={{ cursor: 'pointer', border: 0 }}
      onClick={() => onChange(value => (value === severity ? 'all' : severity))}
    >
      {label}: {count || 0}
    </Badge>
  );
}

function AIFixPreviewModal({ opened, onClose, suggestion, isLoading, error, onApply }) {
  const patches = suggestion?.patches || [];
  return (
    <Modal
      id="ai-report-review-fix-modal"
      opened={opened}
      onClose={onClose}
      title="AI Fix suggestion"
      size="lg"
      centered
    >
      {isLoading && (
        <Flex align="center" justify="center" direction="column" gap="sm" h={180}>
          <Loader />
          <Text c="dimmed">Generating safe fix suggestion...</Text>
        </Flex>
      )}

      {!isLoading && error && (
        <Alert color="red" icon={<TbAlertTriangle />} title="Unable to generate fix">
          {extractApiError(error, 'Please try again.')}
        </Alert>
      )}

      {!isLoading && !error && suggestion && (
        <Stack gap="md">
          <Alert
            color={suggestion.fixable ? 'blue' : 'yellow'}
            icon={<TbSparkles />}
            title={suggestion.title}
          >
            {suggestion.reason}
          </Alert>

          <Box>
            <Text size="sm" fw={600}>
              Suggested action
            </Text>
            <Text size="sm" c="dimmed">
              {suggestion.suggested_action}
            </Text>
          </Box>

          <Group gap="xs">
            <Badge color={suggestion.fixable ? 'green' : 'gray'} variant="light">
              {suggestion.fixable ? 'Fixable' : 'Manual review required'}
            </Badge>
            <Badge
              color={
                suggestion.confidence === 'high'
                  ? 'green'
                  : suggestion.confidence === 'medium'
                    ? 'yellow'
                    : 'gray'
              }
              variant="light"
            >
              Confidence: {suggestion.confidence}
            </Badge>
          </Group>

          {patches.length > 0 && (
            <Stack gap="sm">
              <Text size="sm" fw={600}>
                Proposed changes
              </Text>
              {patches.map((patch, index) => (
                <Paper key={`${patch.table}-${patch.field}-${index}`} withBorder radius="md" p="sm">
                  <Stack gap={6}>
                    <Group gap="xs">
                      <Badge variant="outline">{formatReportName(patch.table)}</Badge>
                      <Badge variant="outline">
                        Row{' '}
                        {Number.isFinite(Number(patch.row_index))
                          ? Number(patch.row_index) + 1
                          : patch.row_index}
                      </Badge>
                      <Badge variant="outline">{formatReportName(patch.field)}</Badge>
                    </Group>
                    <Text size="sm">
                      Change{' '}
                      <Text span fw={700}>
                        {formatReportName(patch.field)}
                      </Text>{' '}
                      from{' '}
                      <Text span fw={700}>
                        {formatEvidenceValue(patch.current_value)}
                      </Text>{' '}
                      to{' '}
                      <Text span fw={700}>
                        {formatEvidenceValue(patch.proposed_value)}
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      {patch.reason}
                    </Text>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}

          {suggestion.validation_notes?.length > 0 && (
            <Box>
              <Text size="sm" fw={600}>
                Validation notes
              </Text>
              <Stack gap={4} mt={4}>
                {suggestion.validation_notes.map((note, index) => (
                  <Text key={index} size="sm" c="dimmed">
                    {note}
                  </Text>
                ))}
              </Stack>
            </Box>
          )}

          <Group justify="flex-end">
            <Button id="ai-report-review-fix-modal-close-button" variant="subtle" onClick={onClose}>
              Close
            </Button>
            <Button
              id="ai-report-review-fix-modal-apply-button"
              disabled={!suggestion.fixable || patches.length === 0}
              onClick={() => onApply(patches)}
            >
              Apply fix locally
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function ExplanationSection({ title, children }) {
  if (!children) return null;
  return (
    <Paper withBorder radius="md" p="md" bg="gray.0">
      <Text size="sm" fw={700}>
        {title}
      </Text>
      <Text size="sm" c="dimmed" mt={6} style={{ whiteSpace: 'pre-wrap' }}>
        {children}
      </Text>
    </Paper>
  );
}

function ExplanationModal({ opened, onClose, explanation, isLoading, error }) {
  return (
    <Modal
      id="ai-report-review-explanation-modal"
      opened={opened}
      onClose={onClose}
      title="AI explanation"
      size="lg"
      centered
    >
      {isLoading && (
        <Flex align="center" justify="center" direction="column" gap="sm" h={180}>
          <Loader />
          <Text c="dimmed">Generating explanation...</Text>
        </Flex>
      )}

      {!isLoading && error && (
        <Alert color="red" icon={<TbAlertTriangle />} title="Unable to explain finding">
          {extractApiError(error, 'Please try again.')}
        </Alert>
      )}

      {!isLoading && !error && explanation && (
        <Stack gap="sm">
          <ExplanationSection title="What this means">
            {explanation.what_this_means || explanation.explanation}
          </ExplanationSection>
          <ExplanationSection title="Why it matters for the filing">
            {explanation.why_this_matters}
          </ExplanationSection>
          <ExplanationSection title="What you should check">
            {explanation.what_to_check_or_fix}
          </ExplanationSection>
          <ExplanationSection title="What not to assume">
            {explanation.what_not_to_assume}
          </ExplanationSection>
        </Stack>
      )}
    </Modal>
  );
}

function AISummarySection({ review }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const summarizeReview = useSummarizeReportReview();
  const summary = summarizeReview.data;

  React.useEffect(() => {
    setIsOpen(false);
    summarizeReview.reset?.();
  }, [review?.review_id]);

  const onGenerateSummary = () => {
    setIsOpen(true);
    summarizeReview.mutate({ review });
  };

  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <TbSparkles size={18} color="#228be6" />
          <Text fw={700}>AI Summary</Text>
          {summary && (
            <Badge color="green" variant="light">
              Generated
            </Badge>
          )}
        </Group>
        <Group gap="xs">
          <Button
            id="ai-report-review-summary-toggle-button"
            size="xs"
            variant="light"
            leftSection={<TbSparkles />}
            loading={summarizeReview.isLoading}
            onClick={onGenerateSummary}
          >
            {summary ? 'Regenerate summary' : 'Generate summary'}
          </Button>
          <Button
            id="ai-report-review-summary-expand-button"
            size="xs"
            variant="subtle"
            onClick={() => setIsOpen(value => !value)}
            disabled={!summary && !summarizeReview.error}
          >
            {isOpen ? 'Hide' : 'Show'}
          </Button>
        </Group>
      </Group>

      <Collapse in={isOpen}>
        <Box mt="md">
          {summarizeReview.isLoading && (
            <Flex align="center" justify="center" direction="column" gap="sm" h={160}>
              <Loader />
              <Text c="dimmed">Creating summary...</Text>
            </Flex>
          )}

          {!summarizeReview.isLoading && summarizeReview.error && (
            <Alert color="red" icon={<TbAlertTriangle />} title="Unable to generate summary">
              {extractApiError(summarizeReview.error, 'Please try again.')}
            </Alert>
          )}

          {!summarizeReview.isLoading && summary && (
            <Stack gap="sm">
              <ExplanationSection title="Executive summary">
                {summary.executive_summary}
              </ExplanationSection>
              <ExplanationSection title="Top priorities">
                {(summary.top_priorities || [])
                  .map((item, index) => `${index + 1}. ${item}`)
                  .join('\n')}
              </ExplanationSection>
              <ExplanationSection title="Actions">
                {(summary.actions || summary.fund_manager_actions || [])
                  .map((item, index) => `${index + 1}. ${item}`)
                  .join('\n')}
              </ExplanationSection>
              <ExplanationSection title="Key risk areas">
                {(summary.key_risk_areas || [])
                  .map((item, index) => `${index + 1}. ${item}`)
                  .join('\n')}
              </ExplanationSection>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export function AIReportReviewDrawer({
  opened,
  onClose,
  review,
  isLoading,
  error,
  onNavigateToFinding,
  onApplyFixPatches,
  fixContext
}) {
  const [selectedFundName, setSelectedFundName] = React.useState('all');
  const [selectedSeverity, setSelectedSeverity] = React.useState('all');
  const [isFixModalOpen, setIsFixModalOpen] = React.useState(false);
  const [isExplanationModalOpen, setIsExplanationModalOpen] = React.useState(false);
  const [fixFindingKey, setFixFindingKey] = React.useState(null);
  const [explanationFindingKey, setExplanationFindingKey] = React.useState(null);
  const suggestFix = useSuggestReportReviewFix();
  const explainFinding = useExplainReportReviewFinding();
  const findings = review?.findings || [];
  const summary = review?.summary || { critical: 0, warning: 0, info: 0 };
  const reviewedAt = formatReviewedAt(review?.reviewed_at);
  const fundOptions = React.useMemo(() => {
    const fundNames = [
      ...new Set(findings.map(finding => finding.fund_name).filter(Boolean))
    ].sort();
    return [
      { value: 'all', label: 'All funds' },
      ...fundNames.map(fundName => ({ value: fundName, label: fundName }))
    ];
  }, [findings]);
  const filteredFindings = findings.filter(finding => {
    const fundMatches = selectedFundName === 'all' || finding.fund_name === selectedFundName;
    const severityMatches = selectedSeverity === 'all' || finding.severity === selectedSeverity;
    return fundMatches && severityMatches;
  });

  React.useEffect(() => {
    setSelectedFundName('all');
    setSelectedSeverity('all');
  }, [review?.review_id]);

  const onSuggestFix = finding => {
    setFixFindingKey(`${finding.rule_id}-${finding.fund_id}-${finding.table}-${finding.field}`);
    setIsFixModalOpen(true);
    suggestFix.mutate({
      periodId: fixContext?.periodId,
      params: fixContext?.params,
      body: { finding }
    });
  };

  const onApplyFix = patches => {
    onApplyFixPatches?.(patches);
    setIsFixModalOpen(false);
  };

  const onExplainFinding = finding => {
    setExplanationFindingKey(
      `${finding.rule_id}-${finding.fund_id}-${finding.table}-${finding.field}`
    );
    setIsExplanationModalOpen(true);
    explainFinding.mutate({ finding });
  };

  return (
    <Drawer
      id="ai-report-review-drawer"
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="8px">
          <TbSparkles size={22} />
          <Title order={4}>AI Report Review</Title>
        </Group>
      }
      styles={{ body: { height: 'calc(100% - 64px)' } }}
    >
      <Stack h="100%" gap="md">
        {isLoading && (
          <Flex align="center" justify="center" direction="column" gap="sm" h="240px">
            <Loader />
            <Text c="dimmed">Reviewing ESMA report...</Text>
          </Flex>
        )}

        {!isLoading && error && (
          <Alert color="red" icon={<TbAlertTriangle />} title="Review failed">
            {extractApiError(error, 'Unable to run report review.')}
          </Alert>
        )}

        {!isLoading && !error && review && (
          <>
            <Paper withBorder radius="md" p="md">
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <Box>
                    <Text fw={600}>Pre-submission summary</Text>
                    <Text size="sm" c="dimmed" mt={4}>
                      {review.ai_summary}
                    </Text>
                  </Box>
                </Group>

                <Group gap="8px">
                  {review.rule_catalog_version && (
                    <Badge color="gray" variant="outline">
                      Catalog: {review.rule_catalog_version}
                    </Badge>
                  )}
                  {reviewedAt && (
                    <Badge color="gray" variant="outline">
                      Reviewed: {reviewedAt}
                    </Badge>
                  )}
                  {review.review_id && (
                    <Badge color="gray" variant="outline">
                      Run: {review.review_id}
                    </Badge>
                  )}
                </Group>
              </Stack>
            </Paper>

            <Alert color="blue" icon={<TbMessageCircle />}>
              This review highlights ESMA data-quality risks before filing. It does not guarantee
              regulator acceptance.
            </Alert>

            <AISummarySection review={review} />

            <Group justify="space-between" align="center">
              <Group gap="xs">
                <Text fw={600}>Findings</Text>
                {(selectedSeverity !== 'all' || selectedFundName !== 'all') && (
                  <Badge variant="light" color="gray">
                    Showing {filteredFindings.length} of {findings.length}
                  </Badge>
                )}
              </Group>
              <Group gap="xs" justify="flex-end">
                <SeverityFilterChip
                  severity="critical"
                  color="red"
                  label="Critical"
                  count={summary.critical}
                  selectedSeverity={selectedSeverity}
                  onChange={setSelectedSeverity}
                />
                <SeverityFilterChip
                  severity="warning"
                  color="yellow"
                  label="Warnings"
                  count={summary.warning}
                  selectedSeverity={selectedSeverity}
                  onChange={setSelectedSeverity}
                />
                <SeverityFilterChip
                  severity="info"
                  color="blue"
                  label="Info"
                  count={summary.info}
                  selectedSeverity={selectedSeverity}
                  onChange={setSelectedSeverity}
                />
                <Select
                  id="ai-report-review-fund-filter"
                  size="xs"
                  w={260}
                  data={fundOptions}
                  value={selectedFundName}
                  onChange={value => setSelectedFundName(value || 'all')}
                  disabled={fundOptions.length <= 1}
                  clearable={false}
                  aria-label="Filter findings by fund"
                />
              </Group>
            </Group>

            <ScrollArea flex={1} offsetScrollbars>
              {findings.length === 0 ? (
                <Paper withBorder radius="md" p="lg">
                  <Group gap="sm">
                    <TbCircleCheck color="green" size={24} />
                    <Box>
                      <Text fw={600}>No review warnings found</Text>
                      <Text size="sm" c="dimmed">
                        The current Datox ESMA pre-submission rules did not identify issues in this
                        report.
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              ) : filteredFindings.length === 0 ? (
                <Paper withBorder radius="md" p="lg">
                  <Text fw={600}>No findings for selected fund</Text>
                  <Text size="sm" c="dimmed">
                    Choose another fund or severity, or switch back to all funds/all severities.
                  </Text>
                </Paper>
              ) : (
                <Stack gap="sm">
                  {filteredFindings.map((finding, index) => (
                    <FindingCard
                      key={`${finding.rule_id}-${index}`}
                      finding={finding}
                      index={index}
                      onNavigateToFinding={onNavigateToFinding}
                      onSuggestFix={onSuggestFix}
                      onExplainFinding={onExplainFinding}
                      isSuggestingFix={
                        suggestFix.isLoading &&
                        fixFindingKey ===
                          `${finding.rule_id}-${finding.fund_id}-${finding.table}-${finding.field}`
                      }
                      isExplaining={
                        explainFinding.isLoading &&
                        explanationFindingKey ===
                          `${finding.rule_id}-${finding.fund_id}-${finding.table}-${finding.field}`
                      }
                    />
                  ))}
                </Stack>
              )}
            </ScrollArea>
          </>
        )}
      </Stack>
      <AIFixPreviewModal
        opened={isFixModalOpen}
        onClose={() => setIsFixModalOpen(false)}
        suggestion={suggestFix.data}
        isLoading={suggestFix.isLoading}
        error={suggestFix.error}
        onApply={onApplyFix}
      />
      <ExplanationModal
        opened={isExplanationModalOpen}
        onClose={() => setIsExplanationModalOpen(false)}
        explanation={explainFinding.data}
        isLoading={explainFinding.isLoading}
        error={explainFinding.error}
      />
    </Drawer>
  );
}
